<template>
	<div class="bean-form">
		<!-- 1. 基本信息 -->
		<v-card variant="border" class="bean-form__section">
			<div class="bean-form__section-title">基本信息</div>
			<div class="bean-form__row">
				<v-text-field
					label="名称"
					v-model="form.name"
					density="compact"
					:error-messages="errors.name"
					class="bean-form__field bean-form__field--name"
					hide-details="auto"
				></v-text-field>
				<v-text-field
					label="编码"
					v-model="form.code"
					density="compact"
					:error-messages="errors.code"
					:hint="form.id ? '' : '按类型自动生成，可改'"
					persistent-hint
					class="bean-form__field bean-form__field--code"
					hide-details="auto"
				></v-text-field>
				<image-uploader v-model="form.cover" label="封面" :error-messages="errors.cover">
					<v-btn color="primary" variant="outlined" size="small">选择封面图</v-btn>
				</image-uploader>
				<v-btn
					icon
					size="small"
					variant="outlined"
					color="secondary"
					:loading="coverPromptLoading"
					@click="copyCoverPrompt"
				>
					<v-icon>$coverPrompt</v-icon>
					<v-tooltip activator="parent" location="top">复制封面提示词</v-tooltip>
				</v-btn>
			</div>
			<div class="bean-form__row mt-4">
				<div class="bean-form__inline">
					<span class="bean-form__label">类型</span>
					<v-radio-group
						v-model="form.kind"
						inline
						density="compact"
						hide-details="auto"
						:error-messages="errors.kind"
						class="mt-0"
					>
						<v-radio
							v-for="item in kindOptions"
							:key="item.value"
							:label="item.title || item.text"
							:value="item.value"
						></v-radio>
					</v-radio-group>
				</div>
				<div class="bean-form__inline">
					<span class="bean-form__label">归属</span>
					<v-radio-group
						v-model="form.owner"
						inline
						density="compact"
						hide-details="auto"
						:error-messages="errors.owner"
						class="mt-0"
					>
						<v-radio
							v-for="item in owners"
							:key="item.value"
							:label="item.text"
							:value="item.value"
						></v-radio>
					</v-radio-group>
					<v-text-field
						v-if="form.owner == 'other'"
						label="其他归属*"
						v-model="form.otherowner"
						density="compact"
						hide-details="auto"
						:error-messages="errors.otherowner"
						class="bean-form__field bean-form__field--other"
						required
					></v-text-field>
				</div>
				<div class="bean-form__inline">
					<span class="bean-form__label">状态</span>
					<v-radio-group v-model="form.status" inline density="compact" hide-details="auto" class="mt-0">
						<v-radio
							v-for="item in refs['BeanStatus']"
							:key="item.value"
							:label="item.title"
							:error-messages="errors.status"
							:value="item.value"
						></v-radio>
					</v-radio-group>
				</div>
			</div>
		</v-card>

		<!-- 2. 产地与豆性 -->
		<section class="bean-form__section">
			<div class="bean-form__section-title">产地与豆性</div>
			<div class="bean-form__row">
				<span class="bean-form__label">
					产地
					<span class="text-medium-emphasis">（{{ isBlend ? '拼配可多选' : '单品单选' }}）</span>
				</span>
				<div class="bean-form__grow">
					<country-selector
						:key="`country-${form.kind || 'single'}`"
						:model-value="countryModel"
						:multiple="isBlend"
						@update:model-value="onCountryUpdate"
					/>
					<div v-if="errors.country" class="text-error text-caption mt-1">{{ errors.country }}</div>
				</div>
			</div>
			<div class="bean-form__row">
				<v-combobox
					label="豆种"
					v-model="varietyList"
					v-model:search="tagSearch.variety"
					:items="varietySuggestions"
					multiple
					chips
					closable-chips
					clearable
					density="compact"
					enterkeyhint="done"
					:error-messages="errors.variety"
					hint="输入后点「添加」，或点键盘上的完成"
					persistent-hint
					class="bean-form__field bean-form__field--lg"
					hide-details="auto"
					@update:search="onTagSearch('variety', $event)"
					@keydown.capture="onTagKeydown($event, 'variety', 'varietyList')"
					@beforeinput="onTagBeforeInput($event, 'variety', 'varietyList')"
					@focusout="onTagFocusout($event, 'variety', 'varietyList')"
				>
					<template #append-inner>
						<v-btn
							v-if="tagDraft.variety && tagDraft.variety.trim()"
							class="bean-tag-add"
							size="small"
							variant="tonal"
							color="primary"
							@pointerdown.stop.prevent="confirmTag('variety', 'varietyList')"
							@mousedown.stop.prevent="confirmTag('variety', 'varietyList')"
						>
							添加
						</v-btn>
					</template>
				</v-combobox>
				<v-combobox
					label="处理方式"
					v-model="processList"
					v-model:search="tagSearch.process"
					:items="processSuggestions"
					multiple
					chips
					closable-chips
					clearable
					density="compact"
					enterkeyhint="done"
					:error-messages="errors.process"
					hint="输入后点「添加」，或点键盘上的完成"
					persistent-hint
					class="bean-form__field bean-form__field--lg"
					hide-details="auto"
					@update:search="onTagSearch('process', $event)"
					@keydown.capture="onTagKeydown($event, 'process', 'processList')"
					@beforeinput="onTagBeforeInput($event, 'process', 'processList')"
					@focusout="onTagFocusout($event, 'process', 'processList')"
				>
					<template #append-inner>
						<v-btn
							v-if="tagDraft.process && tagDraft.process.trim()"
							class="bean-tag-add"
							size="small"
							variant="tonal"
							color="primary"
							@pointerdown.stop.prevent="confirmTag('process', 'processList')"
							@mousedown.stop.prevent="confirmTag('process', 'processList')"
						>
							添加
						</v-btn>
					</template>
				</v-combobox>
				<v-text-field
					label="庄园/处理站"
					v-model="form.processor"
					density="compact"
					:error-messages="errors.processor"
					class="bean-form__field bean-form__field--md"
					hide-details="auto"
				></v-text-field>
				<v-combobox
					label="供应商/贸易商"
					v-model="form.provider"
					density="compact"
					:error-messages="errors.provider"
					:items="providers"
					class="bean-form__field bean-form__field--md"
					hide-details="auto"
				></v-combobox>
			</div>
			<div class="bean-form__row">
				<v-text-field
					label="种植海拔"
					v-model.number="form.altitude"
					type="number"
					density="compact"
					:error-messages="errors.altitude"
					class="bean-form__field bean-form__field--sm"
					hide-details="auto"
					persistent-hint
					hint="如 1800"
				>
					<template #append-inner>m</template>
				</v-text-field>
				<v-text-field
					label="含水率"
					v-model.number="form.moisture"
					density="compact"
					:error-messages="errors.moisture"
					class="bean-form__field bean-form__field--sm"
					hide-details="auto"
					persistent-hint
					:hint="`一般为 12-14%`"
				>
					<template #append-inner>%</template>
				</v-text-field>
				<v-text-field
					label="密度"
					v-model.number="form.density"
					density="compact"
					:error-messages="errors.density"
					hint="一般为 400-600 g/L"
					persistent-hint
					class="bean-form__field bean-form__field--sm"
					hide-details="auto"
				>
					<template #append-inner>g/L</template>
				</v-text-field>
				<v-text-field
					label="目数"
					v-model="form.mesh"
					density="compact"
					:error-messages="errors.mesh"
					hint="如 14/16"
					persistent-hint
					class="bean-form__field bean-form__field--sm"
					hide-details="auto"
				>
					<template #append-inner>目</template>
				</v-text-field>
			</div>
		</section>

		<!-- 3. 价格 -->
		<section class="bean-form__section">
			<div class="bean-form__section-title">价格</div>
			<div class="bean-form__row">
				<v-text-field
					label="生豆采购价"
					v-model.number="form.purchaseprice"
					type="number"
					density="compact"
					:error-messages="errors.purchaseprice"
					clearable
					class="bean-form__field bean-form__field--price"
					hide-details="auto"
					persistent-hint
					:hint="`包含运费、关税等成本`"
				>
					<template #append>
						<span class="bean-form__unit">元/公斤</span>
					</template>
				</v-text-field>
				<v-text-field
					label="熟豆批发基础价"
					v-model.number="form.bprice"
					type="number"
					density="compact"
					:error-messages="errors.bprice"
					clearable
					:hint="`损耗${lossRate}，×${bulkPriceRate}，等级价=基础价×折扣`"
					persistent-hint
					class="bean-form__field bean-form__field--price"
					hide-details="auto"
				>
					<template #append>
						<span class="bean-form__unit">元/公斤</span>
					</template>
				</v-text-field>
				<v-text-field
					label="熟豆零售基础价"
					v-model.number="form.cprice"
					type="number"
					density="compact"
					:error-messages="errors.cprice"
					clearable
					:hint="`损耗${lossRate}，×${retailPriceRate}，等级价=基础价×折扣`"
					persistent-hint
					class="bean-form__field bean-form__field--price"
					hide-details="auto"
				>
					<template #append>
						<span class="bean-form__unit">元/公斤</span>
					</template>
				</v-text-field>
			</div>
		</section>

		<!-- 4. 库存预警 -->
		<section class="bean-form__section">
			<div class="bean-form__section-title">库存预警</div>
			<div class="bean-form__row">
				<v-text-field
					label="生豆预警"
					v-model.number="form.rawstockalert"
					type="number"
					density="compact"
					:error-messages="errors.rawstockalert"
					clearable
					class="bean-form__field bean-form__field--sm"
					hide-details="auto"
				>
					<template #append-inner>克</template>
				</v-text-field>
				<v-text-field
					label="熟豆预警"
					v-model.number="form.roastedstockalert"
					type="number"
					density="compact"
					:error-messages="errors.roastedstockalert"
					clearable
					class="bean-form__field bean-form__field--sm"
					hide-details="auto"
				>
					<template #append-inner>克</template>
				</v-text-field>
			</div>
		</section>

		<!-- 5. 风味 -->
		<section class="bean-form__section bean-form__section--last">
			<div class="bean-form__section-title">风味</div>
			<div class="bean-form__row">
				<v-combobox
					label="风味标签"
					v-model="flavorTags"
					v-model:search="tagSearch.flavor"
					:items="flavorSuggestions"
					multiple
					chips
					closable-chips
					clearable
					density="compact"
					enterkeyhint="done"
					:error-messages="errors.flavor_tags"
					hint="输入后点「添加」，或点键盘上的完成"
					persistent-hint
					class="bean-form__field bean-form__field--xl"
					hide-details="auto"
					@update:search="onTagSearch('flavor', $event)"
					@keydown.capture="onTagKeydown($event, 'flavor', 'flavorTags')"
					@beforeinput="onTagBeforeInput($event, 'flavor', 'flavorTags')"
					@focusout="onTagFocusout($event, 'flavor', 'flavorTags')"
				>
					<template #append-inner>
						<v-btn
							v-if="tagDraft.flavor && tagDraft.flavor.trim()"
							class="bean-tag-add"
							size="small"
							variant="tonal"
							color="primary"
							@pointerdown.stop.prevent="confirmTag('flavor', 'flavorTags')"
							@mousedown.stop.prevent="confirmTag('flavor', 'flavorTags')"
						>
							添加
						</v-btn>
					</template>
				</v-combobox>
			</div>
			<div class="bean-form__row">
				<v-textarea
					v-model="form.description"
					label="风味描述"
					:error-messages="errors.description"
					density="compact"
					rows="2"
					auto-grow
					class="bean-form__field bean-form__field--full"
					hide-details="auto"
				></v-textarea>
			</div>
		</section>

		<bean-similar-panel :draft="submitObject" />

		<div class="bean-form__actions d-flex flex-wrap ga-2">
			<slot name="actions" :object="submitObject"></slot>
		</div>
	</div>
</template>

<script>
import CountrySelector from '../common/CountrySelector.vue'
import ImageUploader from '../common/ImageUploader.vue'
import BeanSimilarPanel from './SimilarPanel.vue'
import { buildCoverPromptFromBean } from '../../utils/nosoCoverPrompt.js'
import { clone } from 'lodash-es'

/** 旧版英文 slug → ISO，兼容历史数据 */
const SLUG_TO_ISO = {
	china: 'CN',
	ethiopia: 'ET',
	vietnam: 'VN',
	colombia: 'CO',
	brazil: 'BR',
	indonesia: 'ID',
	honduras: 'HN',
	india: 'IN',
	uganda: 'UG',
	mexico: 'MX',
	peru: 'PE',
	guatemala: 'GT',
	costa_rica: 'CR',
	nicaragua: 'NI',
	salvador: 'SV',
	el_salvador: 'SV',
	tanzania: 'TZ',
	kenya: 'KE',
	papua_new_guinea: 'PG',
	venezuela: 'VE',
	dominican_republic: 'DO',
	madagascar: 'MG'
}

export default {
	components: {
		ImageUploader,
		CountrySelector,
		BeanSimilarPanel
	},
	props: {
		value: Object,
		errors: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			form: {},
			refs: {},
			varietyList: [],
			processList: [],
			flavorTags: [],
			tagSearch: { variety: '', process: '', flavor: '' },
			tagDraft: { variety: '', process: '', flavor: '' },
			tagDraftToken: { variety: 0, process: 0, flavor: 0 },
			varietySuggestions: [],
			flavorSuggestions: [],
			processSuggestions: ['日晒', '水洗', '蜜处理'],
			kinds: [
				{ text: '单品', value: 'single' },
				{ text: '拼配', value: 'blend' }
			],
			owners: [
				{ text: '店内', value: 'shop' },
				{ text: '其他', value: 'other' }
			],
			providerQuery: '',
			providers: [],
			loading: false,
			coverPromptLoading: false,
			suggestingCode: false,
			lossRate: 0.2,
			//批发价
			bulkPriceRate: 1.5,
			//零售价
			retailPriceRate: 1.7
		}
	},
	computed: {
		kindOptions() {
			return this.refs['BeanKind']?.length ? this.refs['BeanKind'] : this.kinds
		},
		isBlend() {
			return this.form.kind === 'blend'
		},
		countryModel() {
			const codes = this.parseCountryCodes(this.form.country)
			return this.isBlend ? codes : codes[0] || null
		},
		submitObject() {
			return {
				...this.form,
				variety: this.serializeVarieties(this.varietyList),
				process: this.serializeProcesses(this.processList),
				otherprocess: '',
				flavor_tags: this.flavorTags || []
			}
		}
	},
	mounted() {
		this.$api.common.meta('bean', 'BeanType,BeanProcess,BeanStatus,BeanKind', (res) => {
			this.refs = res
		})
		this.fetchProviders()
		this.loadTagSuggestions()
	},
	watch: {
		value: {
			handler(val) {
				this.form = this.normalizeForm(val)
				this.varietyList = this.parseVarieties(this.form.variety)
				this.processList = this.parseProcesses(
					this.form.process,
					this.form.otherprocess || this.form.otherProcess
				)
				this.flavorTags = this.extractFlavorTags(this.form)
				this.tagSearch = { variety: '', process: '', flavor: '' }
				this.tagDraft = { variety: '', process: '', flavor: '' }
				if (!this.form.id) {
					this.suggestCode(this.form.kind || 'single')
				} else if ((!this.form.tags || !this.form.tags.length) && this.form.id) {
					this.loadBeanTags(this.form.id)
				}
			},
			immediate: true
		},
		'form.kind'(val, oldVal) {
			if (!val || val === oldVal) return
			if (!this.form.id) {
				this.suggestCode(val)
			}
			this.syncCountryForKind(val)
		},
		'form.purchaseprice': {
			handler(val) {
				if (val == null || val === '' || Number(val) <= 0) return
				const cost = Number(val) / (1 - this.lossRate)
				// 四舍五入取整，便于后续等级价也落在整数元
				this.form.bprice = Math.round(cost * this.bulkPriceRate)
				this.form.cprice = Math.round(cost * this.retailPriceRate)
			},
			immediate: false
		},
		'form.owner'(val) {
			if (val !== 'other') {
				this.form.otherowner = ''
			}
		}
	},
	methods: {
		// iPad 屏幕键盘没有 Tab。点「添加」或键盘「完成」时，把当前输入收成标签。
		onTagSearch(key, val) {
			const text = val == null ? '' : String(val)
			this.tagDraftToken[key] = (this.tagDraftToken[key] || 0) + 1
			const token = this.tagDraftToken[key]
			if (text.trim()) {
				this.tagDraft[key] = text
				return
			}
			// 延后清空，避免 iPad 失焦时先清空输入、草稿来不及提交
			setTimeout(() => {
				if (this.tagDraftToken[key] !== token) return
				this.tagDraft[key] = ''
			}, 0)
		},
		onTagKeydown(e, key, listKey) {
			if (e.isComposing || e.keyCode === 229) return
			const isEnter = e.key === 'Enter' || e.keyCode === 13
			const isTab = e.key === 'Tab' || e.keyCode === 9
			if (!isEnter && !isTab) return
			if (isEnter) e.preventDefault()
			const text = e.target && 'value' in e.target ? e.target.value : this.tagDraft[key]
			this.tagDraft[key] = text
			this.confirmTag(key, listKey)
		},
		onTagBeforeInput(e, key, listKey) {
			if (e.inputType !== 'insertLineBreak') return
			e.preventDefault()
			const text = (e.target && e.target.value) || this.tagDraft[key]
			this.tagDraft[key] = text
			this.confirmTag(key, listKey)
		},
		confirmTag(key, listKey) {
			const text = this.tagDraft[key] || this.tagSearch[key]
			this.commitTagText(listKey, text)
			this.tagDraftToken[key] = (this.tagDraftToken[key] || 0) + 1
			this.tagDraft[key] = ''
			this.tagSearch[key] = ''
		},
		onTagFocusout(e, key, listKey) {
			const next = e.relatedTarget
			if (next && e.currentTarget?.contains?.(next)) return
			const input = e.currentTarget?.querySelector?.('input')
			const text = (input && input.value) || this.tagDraft[key]
			this.commitTagText(listKey, text)
			this.tagDraft[key] = ''
			this.tagSearch[key] = ''
		},
		commitTagText(listKey, raw) {
			const text = String(raw || '').trim()
			if (!text) return
			const current = Array.isArray(this[listKey]) ? this[listKey] : []
			const exists = current.some((item) => this.tagLabel(item) === text)
			if (exists) return
			this[listKey] = [...current, text]
		},
		tagLabel(item) {
			if (item == null) return ''
			if (typeof item === 'string' || typeof item === 'number') return String(item).trim()
			return String(item.title || item.name || item.value || item.raw || '').trim()
		},
		toIsoCode(code) {
			const raw = String(code || '').trim()
			if (!raw) return ''
			if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase()
			return SLUG_TO_ISO[raw.toLowerCase()] || raw.toUpperCase()
		},
		parseCountryCodes(raw) {
			if (!raw) return []
			if (Array.isArray(raw)) {
				return raw.map((c) => this.toIsoCode(c)).filter(Boolean)
			}
			return String(raw)
				.split(/[,，\s]+/)
				.map((c) => this.toIsoCode(c))
				.filter(Boolean)
		},
		serializeCountries(value) {
			if (Array.isArray(value)) {
				return value
					.map((c) => this.toIsoCode(c))
					.filter(Boolean)
					.join(',')
			}
			return value ? this.toIsoCode(value) : ''
		},
		parseVarieties(raw) {
			if (!raw) return []
			if (Array.isArray(raw)) return raw.map((v) => String(v).trim()).filter(Boolean)
			return String(raw)
				.split(/[/、,，+;|]+/)
				.map((v) => v.trim())
				.filter(Boolean)
		},
		serializeVarieties(list) {
			return (list || [])
				.map((v) => String(v).trim())
				.filter(Boolean)
				.join('、')
		},
		normalizeProcessLabel(raw) {
			const v = String(raw || '').trim()
			if (!v) return ''
			const map = {
				natural: '日晒',
				washed: '水洗',
				honey: '蜜处理',
				other: '其他',
				日晒: '日晒',
				水洗: '水洗',
				蜜处理: '蜜处理'
			}
			return map[v] || map[v.toLowerCase()] || v
		},
		parseProcesses(process, otherProcess) {
			if (Array.isArray(process)) {
				return process.map((p) => this.normalizeProcessLabel(p)).filter(Boolean)
			}
			const raw = String(process || '').trim()
			if (!raw) return []
			// 旧版单选枚举
			if (['natural', 'washed', 'honey', 'other'].includes(raw)) {
				if (raw === 'other') {
					const op = String(otherProcess || '').trim()
					return op ? [op] : ['其他']
				}
				return [this.normalizeProcessLabel(raw)]
			}
			return raw
				.split(/[/、,，+;|]+/)
				.map((p) => this.normalizeProcessLabel(p))
				.filter(Boolean)
		},
		serializeProcesses(list) {
			return (list || [])
				.map((v) => this.normalizeProcessLabel(v))
				.filter(Boolean)
				.join('、')
		},
		extractFlavorTags(form) {
			if (Array.isArray(form.flavor_tags) && form.flavor_tags.length) {
				return [...form.flavor_tags]
			}
			const tags = form.tags || []
			return tags
				.filter((t) => (t.group && t.group.name === '风味') || t.group_name === '风味')
				.map((t) => t.name)
		},
		loadBeanTags(id) {
			this.$http
				.get('/tag/search', { params: { target_type: 'bean', target_id: id } })
				.then((r) => {
					this.form.tags = r?.objects || []
					this.flavorTags = this.extractFlavorTags(this.form)
				})
		},
		loadTagSuggestions() {
			this.$http.get('/tag/getGroups', { params: { type: 'bean' } }).then((groups) => {
				const list = groups || []
				const varietyGroup = list.find((g) => g.name === '品种')
				const flavorGroup = list.find((g) => g.name === '风味')
				const processGroup = list.find((g) => g.name === '处理法')
				this.varietySuggestions = (varietyGroup?.tags || []).map((t) => t.name)
				this.flavorSuggestions = (flavorGroup?.tags || []).map((t) => t.name)
				const preset = ['日晒', '水洗', '蜜处理']
				const fromTags = (processGroup?.tags || []).map((t) => t.name)
				this.processSuggestions = [...new Set([...preset, ...fromTags])]
			})
		},
		async copyCoverPrompt() {
			if (this.coverPromptLoading) return
			this.coverPromptLoading = true
			try {
				// 按当前表单未保存的修改生成，与列表页旧行为一致带上标签风味
				const bean = { ...this.submitObject }
				let tags = this.form.tags || []
				if (!tags.length && bean.id) {
					try {
						const r = await this.$http.get('/tag/search', {
							params: { target_type: 'bean', target_id: String(bean.id) }
						})
						tags = r?.objects || []
						this.form.tags = tags
					} catch (_) {
						tags = []
					}
				}
				if (!tags.length && (this.flavorTags || []).length) {
					tags = this.flavorTags.map((name) => ({ name, group_name: '风味' }))
				}
				const result = buildCoverPromptFromBean(bean, tags)
				const prompt = result?.prompt
				if (!prompt) {
					alert('封面提示词生成失败')
					return
				}
				await this.writeClipboard(prompt)
				alert('封面提示词已复制到剪贴板')
			} catch (e) {
				console.error('copyCoverPrompt', e)
				alert(e?.message || '封面提示词生成失败')
			} finally {
				this.coverPromptLoading = false
			}
		},
		async writeClipboard(text) {
			try {
				if (navigator.clipboard?.writeText) {
					await navigator.clipboard.writeText(text)
					return
				}
			} catch (_) {
				/* fall through */
			}
			const el = document.createElement('textarea')
			el.value = text
			el.setAttribute('readonly', '')
			el.style.position = 'fixed'
			el.style.left = '-9999px'
			document.body.appendChild(el)
			el.select()
			const ok = document.execCommand('copy')
			document.body.removeChild(el)
			if (!ok) throw new Error('复制到剪贴板失败')
		},
		onCountryUpdate(value) {
			this.form.country = this.serializeCountries(value)
		},
		syncCountryForKind(kind) {
			const codes = this.parseCountryCodes(this.form.country)
			if (kind === 'blend') {
				this.form.country = codes.join(',')
			} else {
				this.form.country = codes[0] || ''
			}
		},
		normalizeForm(val) {
			const form = clone(val || {})
			if (!form.kind) {
				const code = String(form.code || '').replace(/\s/g, '')
				if (/^b\d+$/i.test(code)) {
					form.kind = 'blend'
				} else {
					form.kind = 'single'
				}
			}
			if (!form.status) {
				form.status = 'valid'
			}
			// 统一为国家 ISO（拼配用逗号拼接）
			const codes = this.parseCountryCodes(form.country)
			form.country = form.kind === 'blend' ? codes.join(',') : codes[0] || ''
			return form
		},
		suggestCode(kind) {
			if (this.form.id || this.suggestingCode) return
			this.suggestingCode = true
			this.$api.bean.nextCode(
				kind || 'single',
				(res) => {
					this.form.code = res.code
					this.suggestingCode = false
				},
				() => {
					this.suggestingCode = false
				}
			)
		},
		submitForm() {
			this.$emit('submit', this.submitObject)
		},
		fetchProviders(val) {
			this.loading = true
			this.$api.supplier.search(
				{
					name: val,
					type: 'bean'
				},
				(res) => {
					this.providers = res.objects.map((item) => {
						return item.name
					})
				}
			)
		}
	}
}
</script>

<style scoped>
.bean-form {
	width: 100%;
	max-width: 56rem;
}

.bean-form__section {
	margin-bottom: 0.5rem;
	padding-bottom: 0.5rem;
	border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.bean-form__section--last {
	border-bottom: none;
	padding-bottom: 0;
	margin-bottom: 0;
}

.bean-form__section-title {
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0.02em;
	color: rgba(var(--v-theme-on-surface), 0.65);
	margin-bottom: 0.25rem;
}

.bean-form__row {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.5rem 0.75rem;
	margin-bottom: 0.25rem;
}

.bean-form__inline {
	display: inline-flex;
	flex-wrap: wrap;
	align-items: center;
	flex: 0 0 auto;
	gap: 0.25rem 0.5rem;
	min-width: 0;
}

.bean-form__label {
	font-size: 0.75rem;
	color: rgba(var(--v-theme-on-surface), 0.6);
	white-space: nowrap;
	flex: 0 0 auto;
}

.bean-form__grow {
	flex: 1 1 12rem;
	min-width: 0;
}

.bean-tag-add {
	min-width: 44px;
	height: 32px;
	padding: 0 8px;
}

.bean-form__field {
	flex: 0 1 auto;
	min-width: 0;
}

.bean-form__field--sm {
	width: 10rem;
	max-width: 10rem;
}

.bean-form__field--other {
	width: 9rem;
	max-width: 9rem;
}

.bean-form__field--code {
	width: 9rem;
	max-width: 9rem;
}

.bean-form__field--name {
	width: 16rem;
	max-width: 16rem;
}

.bean-form__field--md {
	width: 14rem;
	max-width: 16rem;
}

.bean-form__field--price {
	width: 15rem;
	max-width: 18rem;
	flex: 0 0 auto;
}

.bean-form__unit {
	white-space: nowrap;
	flex-shrink: 0;
	opacity: 0.7;
	font-size: 0.8125rem;
}

.bean-form__field--lg {
	width: 16rem;
	max-width: 20rem;
	flex: 1 1 14rem;
}

.bean-form__field--xl {
	width: 100%;
	max-width: 36rem;
	flex: 1 1 16rem;
}

.bean-form__field--full {
	width: 100%;
	flex: 1 1 100%;
}

.bean-form__inline :deep(.v-radio-group),
.bean-form__inline :deep(.v-selection-control-group) {
	flex: 0 0 auto;
	width: auto !important;
}

.bean-form__actions {
	position: sticky;
	bottom: 0;
	padding-top: 0.375rem;
	background: rgb(var(--v-theme-surface));
	z-index: 1;
}
</style>
